import type { ApiErrorBody } from '@ukinfo/shared';
import { ApiError } from './api';

/**
 * Hochladen mit Fortschrittsanzeige und Abbrechen.
 *
 * `fetch` meldet den Fortschritt eines Uploads nicht; dafuer braucht es
 * XMLHttpRequest (`upload.onprogress`). Fehlermeldungen des Servers kommen als
 * ApiError an, damit die Oberflaeche sie wie jede andere anzeigen kann.
 */

/** Der Upload wurde mit `abort()` beendet. */
export class UploadAbortedError extends Error {
  constructor() {
    super('Das Hochladen wurde abgebrochen.');
    this.name = 'UploadAbortedError';
  }
}

export interface UploadHandle<T> {
  promise: Promise<T>;
  abort: () => void;
}

export function uploadWithProgress<T>(
  path: string,
  formData: FormData,
  onProgress: (loaded: number, total: number) => void,
): UploadHandle<T> {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', path);
    xhr.withCredentials = true;
    xhr.responseType = 'text';
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded, event.total);
    };
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T);
        return;
      }
      // Antworten von Caddy (zu gross) sind kein JSON - dann hilft ein eigener Satz.
      const fallback = xhr.status === 413 ? 'Das Video ist zu groß für den Server.' : 'Das Hochladen ist fehlgeschlagen.';
      reject(new ApiError(xhr.status, body as ApiErrorBody | null, fallback));
    };
    xhr.onerror = () => reject(new ApiError(0, null, 'Die Verbindung ist abgebrochen. Bitte prüfe das Netz und versuche es noch einmal.'));
    xhr.ontimeout = () => reject(new ApiError(0, null, 'Das Hochladen hat zu lange gedauert. Bitte versuche es noch einmal.'));
    xhr.onabort = () => reject(new UploadAbortedError());
    xhr.send(formData);
  });
  return { promise, abort: () => xhr.abort() };
}
